import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Task, TaskPriority, TaskStatus, Sprint, TaskType } from '../../types';
import { getProjectTasks, updateTaskStatus } from '../../services/taskService';
import { getProjectSprints, moveTaskToSprint, SprintStatus, SPRINT_STATUS_LABEL } from '../../services/sprintService';
import TaskDetailDrawer from '../../task/TaskDetailDrawer';
import NewTaskModal from '../../task/NewTaskModal';

interface Props { pca: IPublicClientApplication; projectId: string; refreshKey?: number }

const PRIORITY: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskPriority.Critical]: { bg: '#DC2626', fg: '#fff', label: 'Critical' },
  [TaskPriority.High]:     { bg: '#E59F1C', fg: '#1C1917', label: 'High' },
  [TaskPriority.Medium]:   { bg: '#2563EB', fg: '#fff', label: 'Medium' },
  [TaskPriority.Low]:      { bg: '#6B7280', fg: '#fff', label: 'Low' },
};
const STATUS_LABEL: Record<number, string> = {
  [TaskStatus.NotStarted]: 'Not Started', [TaskStatus.InProgress]: 'In Progress',
  [TaskStatus.InReview]: 'In Review', [TaskStatus.Done]: 'Done',
  [TaskStatus.OnHold]: 'On Hold', [TaskStatus.Cancelled]: 'Cancelled',
};
const STATUS_ORDER = [TaskStatus.NotStarted, TaskStatus.InProgress, TaskStatus.InReview, TaskStatus.Done, TaskStatus.OnHold, TaskStatus.Cancelled];
const DAY = 24 * 60 * 60 * 1000;

function initials(name?: string | null): string {
  if (!name) return '';
  const p = name.trim().split(/\s+/).filter(Boolean);
  return p.length === 0 ? '' : (p.length === 1 ? p[0][0] : p[0][0] + p[p.length - 1][0]).toUpperCase();
}
function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
const bucket = (s: number): 'todo' | 'inprogress' | 'done' =>
  s === TaskStatus.Done ? 'done' : (s === TaskStatus.InProgress || s === TaskStatus.InReview) ? 'inprogress' : 'todo';

export default function SprintView({ pca, projectId, refreshKey }: Props) {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprintId, setSprintId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Task | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ done: true });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([getProjectSprints(pca, projectId), getProjectTasks(pca, projectId)]);
      setSprints(s); setTasks(t);
      setSprintId(prev => {
        if (prev && s.some(x => x.dagrun_pm_sprintid === prev)) return prev;
        const active = s.find(x => x.dagrun_pm_status === SprintStatus.Active);
        return active?.dagrun_pm_sprintid ?? s[0]?.dagrun_pm_sprintid ?? '';
      });
    } finally { setLoading(false); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload, refreshKey]);

  const sprint = sprints.find(s => s.dagrun_pm_sprintid === sprintId) ?? null;
  const sprintTasks = useMemo(() => tasks.filter(t => t._dagrun_pm_sprintid_value === sprintId), [tasks, sprintId]);

  const metrics = useMemo(() => {
    const planned = sprintTasks.reduce((a, t) => a + (t.dagrun_pm_storypoints ?? 0), 0);
    const completed = sprintTasks.filter(t => t.dagrun_pm_status === TaskStatus.Done).reduce((a, t) => a + (t.dagrun_pm_storypoints ?? 0), 0);
    const remaining = Math.max(0, planned - completed);
    let daysLeft = 0, velocity = 0, atRisk = false;
    if (sprint?.dagrun_pm_enddate) {
      daysLeft = Math.max(0, Math.ceil((new Date(sprint.dagrun_pm_enddate).getTime() - Date.now()) / DAY));
    }
    if (sprint?.dagrun_pm_startdate && sprint?.dagrun_pm_enddate) {
      const durDays = Math.max(1, Math.round((new Date(sprint.dagrun_pm_enddate).getTime() - new Date(sprint.dagrun_pm_startdate).getTime()) / DAY));
      velocity = planned / durDays;
    }
    if (daysLeft > 0) atRisk = (remaining / daysLeft) > velocity;
    else atRisk = remaining > 0;
    return { planned, completed, remaining, daysLeft, atRisk };
  }, [sprintTasks, sprint]);

  const changeStatus = async (task: Task, status: number) => {
    setTasks(prev => prev.map(t => t.dagrun_pm_taskid === task.dagrun_pm_taskid ? { ...t, dagrun_pm_status: status } : t));
    try { await updateTaskStatus(pca, task.dagrun_pm_taskid, status); } catch { reload(); }
  };
  const moveToBacklog = async (task: Task) => {
    setTasks(prev => prev.filter(t => t.dagrun_pm_taskid !== task.dagrun_pm_taskid));
    try { await moveTaskToSprint(pca, task.dagrun_pm_taskid, null); } catch { reload(); }
  };

  if (loading) return <div className="spinner" style={{ margin: '60px auto' }} />;

  if (sprints.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
        No sprints yet — create one in Settings to start a sprint.
      </div>
    );
  }

  const groups: { key: 'todo' | 'inprogress' | 'done'; label: string }[] = [
    { key: 'todo', label: 'To Do' }, { key: 'inprogress', label: 'In Progress' }, { key: 'done', label: 'Done' },
  ];
  const noActive = !sprints.some(s => s.dagrun_pm_status === SprintStatus.Active);

  return (
    <div style={{ padding: '16px 24px', height: '100%', overflowY: 'auto' }}>
      {/* Sprint selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <select value={sprintId} onChange={e => setSprintId(e.target.value)}
          style={{ fontSize: 13, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'var(--font-ui)', minWidth: 220 }}>
          {sprints.map(s => <option key={s.dagrun_pm_sprintid} value={s.dagrun_pm_sprintid}>{s.dagrun_pm_name}</option>)}
        </select>
      </div>
      {noActive && (
        <div style={{ padding: '8px 12px', marginBottom: 12, background: '#FEF3C7', borderLeft: '3px solid #D97706', color: '#92400E', fontSize: 12, borderRadius: 4 }}>
          No active sprint — select a sprint or start one in Settings.
        </div>
      )}

      {sprint && (
        <>
          {/* Sprint header */}
          <div style={{ marginBottom: 6, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{sprint.dagrun_pm_name}</h3>
            <span style={{ fontSize: 13, color: 'var(--ink-3)', fontFamily: 'var(--font-mono)' }}>{fmt(sprint.dagrun_pm_startdate)} – {fmt(sprint.dagrun_pm_enddate)}</span>
            <span style={{ fontSize: 11, fontWeight: 600, background: '#D4EEF5', color: '#0A5568', padding: '2px 8px', borderRadius: 10 }}>{SPRINT_STATUS_LABEL[sprint.dagrun_pm_status]}</span>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-2)', fontFamily: 'var(--font-mono)' }}>{metrics.completed} / {metrics.planned} pts</span>
          </div>
          {sprint.dagrun_pm_sprintgoal && <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 10 }}>{sprint.dagrun_pm_sprintgoal}</div>}
          <div style={{ background: 'var(--surface-2)', borderRadius: 3, height: 6, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ height: '100%', width: `${metrics.planned > 0 ? Math.round((metrics.completed / metrics.planned) * 100) : 0}%`, background: 'var(--brand)' }} />
          </div>
          {/* Burn-down indicator (text only) */}
          <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', marginBottom: 16, color: 'var(--ink-3)' }}>
            {metrics.remaining} points remaining · {metrics.daysLeft} day{metrics.daysLeft === 1 ? '' : 's'} left ·{' '}
            <span style={{ color: metrics.atRisk ? '#DC2626' : '#16A34A', fontWeight: 600 }}>{metrics.atRisk ? 'At risk' : 'On track'}</span>
          </div>

          {/* Grouped task list */}
          {groups.map(g => {
            const items = sprintTasks.filter(t => bucket(t.dagrun_pm_status) === g.key);
            const isCollapsed = !!collapsed[g.key];
            return (
              <div key={g.key} style={{ marginBottom: 14 }}>
                <button type="button" onClick={() => setCollapsed(c => ({ ...c, [g.key]: !c[g.key] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 0', fontFamily: 'var(--font-ui)', textAlign: 'left' }}>
                  <span style={{ fontSize: 9, transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.15s' }}>▶</span>
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-2)' }}>{g.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>{items.length}</span>
                </button>
                {!isCollapsed && (
                  <div>
                    {items.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-5)', padding: '4px 0 8px 22px' }}>No tasks</div>}
                    {items.map(t => {
                      const prio = t.dagrun_pm_priority != null ? PRIORITY[t.dagrun_pm_priority] : null;
                      return (
                        <div key={t.dagrun_pm_taskid}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 6, border: '1px solid var(--border-light)', borderRadius: 7, background: '#fff' }}>
                          {prio
                            ? <span style={{ background: prio.bg, color: prio.fg, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 3, flexShrink: 0 }}>{prio.label}</span>
                            : <span style={{ width: 4 }} />}
                          <button type="button" onClick={() => setSelected(t)}
                            style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)' }}>
                            {t.dagrun_pm_name}
                          </button>
                          {t.dagrun_pm_storypoints != null && (
                            <span title="Story points" style={{ fontSize: 11, fontWeight: 600, background: 'var(--surface-2)', color: 'var(--ink-2)', padding: '2px 8px', borderRadius: 10, flexShrink: 0 }}>{t.dagrun_pm_storypoints}</span>
                          )}
                          {t.dagrun_pm_assigneeid && (
                            <span title={t.dagrun_pm_assigneename ?? ''} style={{ width: 24, height: 24, borderRadius: '50%', background: '#0E7490', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(t.dagrun_pm_assigneename)}</span>
                          )}
                          <select value={t.dagrun_pm_status} onChange={e => changeStatus(t, Number(e.target.value))}
                            style={{ fontSize: 11, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, flexShrink: 0, width: 'auto', maxWidth: 160 }}>
                            {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                          </select>
                          <button type="button" onClick={() => moveToBacklog(t)} title="Move to backlog" aria-label="Move to backlog"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 14, lineHeight: 1, flexShrink: 0 }}
                            onMouseEnter={e => (e.currentTarget.style.color = 'var(--danger)')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}>×</button>
                        </div>
                      );
                    })}
                    {g.key === 'todo' && (
                      <button type="button" onClick={() => setShowNew(true)}
                        style={{ marginTop: 4, background: '#D4EEF5', border: '1px solid #0E7490', color: '#0E7490', borderRadius: 6, padding: '6px 12px', fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        + Add Task to Sprint
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      <TaskDetailDrawer task={selected} pca={pca} onClose={() => setSelected(null)} onSaved={() => reload()} />

      {showNew && (
        <NewTaskModal pca={pca} projectId={projectId} agile initialType={TaskType.Story} initialSprintId={sprintId}
          onCreated={() => { setShowNew(false); reload(); }}
          onCancel={() => setShowNew(false)} />
      )}
    </div>
  );
}
